import { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { groupChatService } from '@/services/groupChatService'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface CreateGroupDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onGroupCreated?: (conversationId: string) => void
}

/**
 * CreateGroupDialog Component
 * Dialog for creating a new generic group chat
 */
export default function CreateGroupDialog({
    open,
    onOpenChange,
    onGroupCreated,
}: CreateGroupDialogProps) {
    const { t } = useTranslation('common')
    const [groupName, setGroupName] = useState('')
    const [isPublic, setIsPublic] = useState(false)
    const [maxParticipants, setMaxParticipants] = useState<string>('')
    const [isCreating, setIsCreating] = useState(false)

    const handleCreate = async () => {
        if (!groupName.trim()) {
            toast.error(t('chat.createGroup.errors.nameRequired'))
            return
        }

        setIsCreating(true)

        try {
            const data = {
                name: groupName.trim(),
                isPublic,
                ...(maxParticipants && {
                    maxParticipants: parseInt(maxParticipants, 10),
                }),
            }

            const response = await groupChatService.createGenericGroup(data)

            toast.success(t('chat.createGroup.success', { name: groupName }))

            // Reset form
            setGroupName('')
            setIsPublic(false)
            setMaxParticipants('')

            // Close dialog
            onOpenChange(false)

            // Notify parent component
            if (onGroupCreated && response.id) {
                onGroupCreated(response.id)
            }
        } catch (error: any) {
            // console.error('Error creating group:', error)
            toast.error(
                error.response?.data?.message || t('chat.createGroup.errors.createFailed')
            )
        } finally {
            setIsCreating(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{t('chat.createGroup.title')}</DialogTitle>
                    <DialogDescription>
                        {t('chat.createGroup.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    {/* Group name */}
                    <div className="grid gap-2">
                        <Label htmlFor="groupName">{t('chat.createGroup.groupNameRequired')}</Label>
                        <Input
                            id="groupName"
                            placeholder={t('chat.createGroup.groupNamePlaceholder')}
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            disabled={isCreating}
                        />
                    </div>

                    {/* Max participants */}
                    <div className="grid gap-2">
                        <Label htmlFor="maxParticipants">
                            {t('chat.createGroup.maxParticipants')}
                        </Label>
                        <Input
                            id="maxParticipants"
                            type="number"
                            min="2"
                            placeholder={t('chat.createGroup.maxParticipantsPlaceholder')}
                            value={maxParticipants}
                            onChange={(e) => setMaxParticipants(e.target.value)}
                            disabled={isCreating}
                        />
                    </div>

                    {/* Public group checkbox */}
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="isPublic"
                            checked={isPublic}
                            onCheckedChange={(checked) => setIsPublic(checked as boolean)}
                            disabled={isCreating}
                        />
                        <Label
                            htmlFor="isPublic"
                            className="text-sm font-normal cursor-pointer"
                        >
                            {t('chat.createGroup.makePublic')}
                        </Label>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isCreating}
                    >
                        {t('chat.createGroup.cancel')}
                    </Button>
                    <Button onClick={handleCreate} disabled={isCreating}>
                        {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isCreating ? t('chat.createGroup.creating') : t('chat.createGroup.create')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
